import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { translations } from '../../../../configs/translations'
import { Language } from '../../../../context/LanguageContext'
import { ChatMessage, MessagesResponse } from '../../../../models/ChatDto'
import { UserProfile } from '../../../../models/UserProfile'
import { EventSourceConnection } from '../../../../services/EventSourceConnection'
import UserService from '../../../../services/UserService'
import { formatDate } from '../../../../utils/formatDate'
import { formatText } from '../../../../utils/formatText'
import './Chat.css'

interface ChatProps {
    id: number,
    name: string,
    right?: number,
    isOpen?: boolean,
    setIsChatOpen: React.Dispatch<React.SetStateAction<boolean>>,
}

export const Chat = ({ id, name, right, isOpen, setIsChatOpen }: ChatProps) => {
    const [language, setLanguage] = useState<Language>('ru')
    const t = translations[language]

    const [isShown, setIsShown] = useState(false)
    const [inputText, setInputText] = useState('')

    const audio = new Audio('/sounds/open_chat.mp3')
    audio.volume = 0.05

    const audioSend = new Audio('/sounds/message_sent.mp3')
    audioSend.volume = 0.05

    const messagesEndRef = useRef<HTMLDivElement>(null)
    const messagesListRef = useRef<HTMLDivElement>(null)
    const [userProfile, setUserProfile] = useState<UserProfile | null>(JSON.parse(localStorage.getItem('profile') || 'null'))

    const [messages, setMessages] = useState<MessagesResponse | null>(null)

    const fetchMessages = async () => {
        await UserService.getMessages(id)
            .then((response) => {
                const sorted = response.messages.sort((a, b) => {
                    if (!a.timestamp || !b.timestamp) return 0
                    return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
                })
                setMessages({ messages: sorted })
            })
            .catch((error) => {
                toast.error(
                    error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : t.error_unknown,
                    { className: 'notification-error' }
                )
            })
    }

    const handleMessage = (msg: ChatMessage) => {
        setMessages(prev => prev
            ? { messages: [...prev.messages, msg] }
            : { messages: [msg] }
        )
    }

    const handleRead = (messageIds: string[]) => {
        setMessages(prev => prev
            ? {
                messages: prev.messages.map(msg => {
                    if (messageIds && messageIds.length > 0 && messageIds.includes(msg.id)) {
                        return { ...msg, read: true }
                    }
                    return msg
                })
            }
            : { messages: [] }
        )
    }

    const handleChat = (messageIds: string[]) => {
        setMessages(prev => prev
            ? {
                messages: prev.messages.map(msg => {
                    if (messageIds && messageIds.length > 0 && messageIds.includes(msg.id)) {
                        return { ...msg, read: true }
                    }
                    return msg
                })
            }
            : { messages: [] }
        )
    }

    useEffect(() => {
        if (!isOpen) return

        fetchMessages()

        const sse = new EventSourceConnection(UserService.getMessagesSSE)
        sse.registerListener<string[]>("CHAT", handleChat)
        sse.registerListener<string[]>("READ", handleRead)
        sse.registerListener<ChatMessage>("MESSAGE", handleMessage)

        return () => {
            sse.close()
        }
    }, [id, isOpen])

    const markMessagesAsRead = useCallback(async () => {
        if (!messages?.messages) return

        const unreadMessages = messages.messages.filter(msg =>
            !msg.read &&
            !msg.author &&
            msg.id &&
            msg.id !== 'justSent' // Skip temporary messages that haven't been saved yet
        )

        if (unreadMessages.length === 0) return

        try {
            const messageIds = unreadMessages.map(msg => msg.id)

            await UserService.markMessagesAsRead(id, messageIds)
                .then(() => {
                    setMessages(prev => {
                        if (!prev) return null
                        return {
                            ...prev,
                            messages: prev.messages.map(msg => {
                                if (messageIds && messageIds.length > 0 && messageIds.includes(msg.id)) {
                                    return { ...msg, read: true }
                                }
                                return msg
                            })
                        }
                    })
                })
                .catch((error) => {
                    toast.error(
                        error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                            ? t[error.response.data as keyof typeof t]
                            : t.error_unknown,
                        { className: 'notification-error' }
                    )
                })
        } catch (error) {
            console.error('Error marking messages as read:', error)
        }
    }, [messages])

    // Auto-scroll to bottom when chat is opened or messages change
    useEffect(() => {
        if (isOpen && messagesListRef.current) {
            // Force scroll to bottom immediately
            messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight

            // And again after a small delay to ensure it sticks
            const timer = setTimeout(() => {
                if (messagesListRef.current) {
                    messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight
                }
            }, 100)

            return () => clearTimeout(timer)
        }
    }, [isOpen, messages])

    // Mark messages as read when chat is opened or messages change
    useEffect(() => {
        if (isOpen && messages?.messages?.length) {
            // Check if the chat is not scrollable (few messages)
            if (messagesListRef.current) {
                const { scrollHeight, clientHeight } = messagesListRef.current
                const isNotScrollable = scrollHeight <= clientHeight + 10 // 10px threshold

                if (isNotScrollable) {
                    markMessagesAsRead()
                }
            }
        }
    }, [isOpen, messages, markMessagesAsRead])

    const handleScroll = useCallback(() => {
        if (messagesListRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = messagesListRef.current
            const isNearBottom = scrollHeight - scrollTop <= clientHeight + 100

            if (isNearBottom) {
                markMessagesAsRead()
            }
        }
    }, [markMessagesAsRead])

    useEffect(() => {
        const list = messagesListRef.current
        if (list) {
            list.addEventListener('scroll', handleScroll)
            return () => list.removeEventListener('scroll', handleScroll)
        }
    }, [handleScroll])

    useEffect(() => {
        if (isOpen) {
            setIsShown(true)
            audio.play()
        } else {
            setTimeout(() => setIsShown(false), 225)
        }
    }, [isOpen])

    const sendMessage = async () => {
        if (!inputText || inputText.trim() === '') return

        const message: ChatMessage = {
            id: 'justSent',
            content: inputText,
            author: true,
            authorName: userProfile?.userName || '{username}',
            read: false,
            isDelivered: false,
            timestamp: new Date(),
        }

        await UserService.sendMessage(id, message.content)
            .then((response) => {
                setMessages((prev) => {
                    if (!prev) return { messages: [message] }
                    return { messages: [...prev.messages, { ...message, isDelivered: true, id: response }] }
                })
            })
            .catch((error) => {
                toast.error(
                    error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : t.error_unknown,
                    { className: 'notification-error' }
                )
            })

        setInputText('')
    }

    const getImage = (message: ChatMessage) => {
        if (message.read) {
            return <img src="/icons/24px/read.svg" alt="read" />
        }
        if (message.isDelivered === false) {
            return <img src="/icons/24px/sent.svg" alt="sent" />
        }
        return <img src="/icons/24px/delivered.svg" alt="delivered" />
    }

    return (
        <>
            {isShown && messages &&
                <div className={`chat ${isOpen ? 'show-in' : 'hide-out'}`} style={{ right: right ? right : '110px' }}>
                    <div className='chat-header'>
                        <p className='chat-title'>{name}</p>
                        <div className='chat-header-buttons'>
                            <div className='chat-minimize-button' onClick={() => setIsChatOpen(false)}>
                                <img src="/icons/minimize.png" alt="minimize" />
                            </div>
                            {name != "Global" &&
                                <div className='chat-close-button' onClick={() => setIsChatOpen(false)}>
                                    <img src="/icons/close.png" alt="close" />
                                </div>
                            }
                        </div>
                    </div>

                    <div
                        className='messages-list'
                        ref={messagesListRef}
                        onScroll={handleScroll}
                    >

                        {messages?.messages.map((message, index) => (
                            <div className='message' key={index}>
                                <p className='message-text'>
                                    <span className={`message-text-author ${message.author ? 'author' : ''}`}>
                                        {
                                            `[${formatDate(message.timestamp, { withHours: true })}] ${formatText(message.authorName, 10)}: `
                                        }
                                    </span>
                                    {formatText(message.content, 121)}
                                </p>
                                <div className='message-delivered-icon'>
                                    {getImage(message)}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div ref={messagesEndRef} />
                    <form className='chat-input-container'
                        onSubmit={(e) => {
                            e.preventDefault()
                            sendMessage()
                        }}
                    >
                        <input
                            type="text"
                            placeholder={t.input_message}
                            maxLength={120}
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                        />
                        <div
                            className='chat-send-button'
                            onClick={async () => await sendMessage()}
                        >
                            <img src="/icons/send.png" alt="send" />
                        </div>
                    </form>
                </div>}
        </>
    )
}